import { useEffect, useRef, useState } from "react";
import { observer } from "@legendapp/state/react";
import KnownWordToast from "@/components/feedback/KnownWordToast";
import { wordKnownToast$ } from "../../state/videoState";

const AUTO_DISMISS_MS = 1900;

/**
 * App-level bridge that drives the shared `KnownWordToast` (the canonical
 * iOS-parity "You added this word to known" confirmation, also used inline by
 * lessons + Flight School) from the feed's `wordKnownToast$` counter.
 *
 * The feed renders MANY VideoLayout instances, so it can't use the inline
 * `useKnownWordToast` hook the way a single lesson page does — it bumps this
 * counter instead and this single mount point shows the toast. Keeps one
 * consistent known-word toast across feed, lessons, and Flight School.
 */
export const FeedKnownWordToast = observer(function FeedKnownWordToast() {
  const seq = wordKnownToast$.get();
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (seq === 0) return; // nothing marked known yet
    setMessage("You added this word to known");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(null), AUTO_DISMISS_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [seq]);

  return <KnownWordToast message={message} />;
});
